import { useEffect } from 'react';

/** Gap between a trigger and its panel, and the panel's clearance from the viewport edge. */
export const PANEL_OFFSET_PX = 8;

/**
 * Places a body-level panel under its trigger: fixed position, pinned to the trigger's left
 * edge, and shifted left when it would run past the viewport (a trigger near the right edge,
 * e.g. the rightmost export progress button or the Formation column). The one placement rule for
 * every popover that portals its panel to the document body (WorkflowStatusPopover,
 * CompositeWellboreLegs), so they read the same wherever their trigger sits.
 *
 * @param {HTMLElement} trigger - The element the panel hangs from.
 * @param {HTMLElement} panel - The portaled panel.
 * @return {DOMRect} The trigger's rect, for a caller that places vertically on top of this.
 */
export function placePanelBelow(
  trigger: HTMLElement,
  panel: HTMLElement
): DOMRect {
  const rect = trigger.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;
  const left = Math.max(
    PANEL_OFFSET_PX,
    Math.min(rect.left, viewportWidth - PANEL_OFFSET_PX - panel.offsetWidth)
  );
  panel.style.position = 'fixed';
  panel.style.top = `${rect.bottom + PANEL_OFFSET_PX}px`;
  panel.style.left = `${left}px`;
  return rect;
}

/**
 * Keeps an open, body-level panel on its trigger: places it on open, then again on every resize
 * and scroll (capture phase, to see a table's own scroller as well as the page), one animation
 * frame at a time. Fixed positioning means the panel never moves with its scroll container, so
 * re-placing is what makes it follow.
 *
 * @param {boolean} open - Whether the panel is open; nothing listens while it is closed.
 * @param {() => void} place - Places the panel; wrap it in useCallback, since a new function
 *   re-registers the listeners.
 * @return {void}
 */
export function useAnchoredPanel(open: boolean, place: () => void): void {
  useEffect(() => {
    if (!open) return undefined;
    let frame: number | null = null;
    let pending = false;
    /**
     * Re-places the panel on the next animation frame, coalescing a burst of events. `pending`
     * rather than the frame id, so a frame that runs its callback before the id is handed back
     * (a test stub, or a browser flushing) still clears the way for the next one.
     *
     * @return {void}
     */
    const schedule = () => {
      if (pending) return;
      pending = true;
      frame = requestAnimationFrame(() => {
        pending = false;
        place();
      });
    };
    place();
    window.addEventListener('resize', schedule);
    document.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    });
    return () => {
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, { capture: true });
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [open, place]);
}
